/**
 * GET /api/v1/shop/products and GET /api/v1/media/products/{assetId} (v0.1.70): the Ürünler
 * page. Sercan, 2026-09-30: "elimizdeki tüm araçlar ve onların kartlarını ben şu an
 * görebiliyor muyum, fotoğraflarıyla?". Every product card of shop.products with its vehicle,
 * the files it ships (package_contents: name, size, coverage notes, measured extent), the
 * primary geometry card, the Etsy listing and its images (twin.assets renders, previews and
 * photos of our own files; never buyer data). The bytes of an image come from the media route
 * by asset id, as a thumbnail (products/thumbs/<id>.webp when one was imported, else the
 * original) or in full. Panel only; the bot never reads it. Instants are epoch milliseconds,
 * money is cents.
 */
import { z } from 'zod';
const instant = z.number().int().nonnegative();
const count = z.number().int().nonnegative();
export const PRODUCT_STATUSES = ['draft', 'active', 'inactive', 'retired'];
export const PRODUCT_STATUS_LABELS_TR = Object.freeze({
    draft: 'Taslak',
    active: 'Aktif',
    inactive: 'Pasif',
    retired: 'Emekli',
});
/** v0.1.235 (migration 0111): 'model' is an STL made from CAD data or a model maker's file, never called a scan. */
export const PRODUCT_TYPES = ['scan', 'plan', 'step', 'bundle', 'license', 'sample', 'custom', 'model'];
export const PRODUCT_TYPE_LABELS_TR = Object.freeze({
    scan: '3B tarama',
    plan: 'Plan',
    step: 'STEP',
    bundle: 'Paket',
    license: 'Lisans',
    sample: 'Örnek',
    custom: 'Özel',
    model: '3B model',
});
export const PRODUCT_COVERAGES = [
    'full_vehicle',
    'front',
    'rear',
    'chassis',
    'interior',
    'underbody',
    'region',
    'part',
    'n_a',
];
export const PRODUCT_COVERAGE_LABELS_TR = Object.freeze({
    full_vehicle: 'Tüm araç',
    front: 'Ön',
    rear: 'Arka',
    chassis: 'Şasi',
    interior: 'İç mekân',
    underbody: 'Alt takım',
    region: 'Bölge',
    part: 'Parça',
    n_a: 'Belirsiz',
});
/** What the archive review found about the cabin of a scanned file (package_contents.cabin_interior). */
export const CABIN_INTERIOR_VALUES = ['yes', 'no', 'partial', 'n_a'];
export const CABIN_INTERIOR_LABELS_TR = Object.freeze({
    yes: 'Kabin içi var',
    no: 'Kabin içi yok',
    partial: 'Kabin içi kısmen',
    n_a: 'Kabin içi: uygulanmaz',
});
/** Coverage words the archive review writes into a file entry (package_contents.coverage). */
export const FILE_COVERAGE_LABELS_TR = Object.freeze({
    exterior: 'dış yüzey',
    interior: 'kabin içi',
    underbody: 'alt takım',
    chassis: 'şasi',
    frame: 'şasi',
    wheels: 'tekerlekler',
    bed: 'kasa',
    tailgate: 'bagaj kapağı',
    mirrors: 'aynalar',
    engine_bay: 'motor bölmesi',
    engine_bay_partial: 'motor bölmesi (kısmen)',
    roof: 'tavan',
    roof_rack: 'tavan rafı',
    doors: 'kapılar',
    no_doors: 'kapılar yok',
    seats: 'koltuklar',
    dashboard: 'torpido',
    bumper: 'tampon',
    front: 'ön',
    rear: 'arka',
    glass: 'camlar',
    suspension: 'süspansiyon',
    exhaust: 'egzoz',
    fuel_tank: 'yakıt deposu',
    spare_wheel: 'yedek lastik',
    bedliner: 'kasa astarı',
    grille: 'ızgara',
    hood: 'kaput',
    lights: 'farlar',
    no_wheels: 'tekerlekler yok',
});
export const PRODUCT_IMAGE_KINDS = ['render', 'preview', 'photo'];
export const PRODUCT_IMAGE_KIND_LABELS_TR = Object.freeze({
    render: 'Render',
    preview: 'Önizleme',
    photo: 'Fotoğraf',
});
/** A size of the media route: the thumbnail for cards and grids, the original for a close look. */
export const PRODUCT_IMAGE_SIZES = ['thumb', 'full'];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** The api path (below the base path) of an image's bytes. */
export function productImagePath(assetId, size) {
    return `/media/products/${assetId}?size=${size}`;
}
export const productImageSchema = z.object({
    assetId: z.string().regex(UUID),
    kind: z.enum(PRODUCT_IMAGE_KINDS),
    /** The manifest title: file name, triangle count, extent and the Turkish coverage tag. */
    title: z.string().nullable(),
    mimeType: z.string(),
    bytes: count.nullable(),
});
export const productFileSchema = z.object({
    name: z.string(),
    bytes: count.nullable(),
    /** Upper-case extension (STL, OBJ, STEP, DXF, PDF) when the name has one. */
    format: z.string().nullable(),
    /** The archive review's note about what the file covers, English as written. */
    note: z.string().nullable(),
    coverage: z.array(z.string()),
    cabinInterior: z.enum(CABIN_INTERIOR_VALUES).nullable(),
    /** Length x width x height in millimetres, measured from the file. */
    extentMm: z.tuple([z.number(), z.number(), z.number()]).nullable(),
    triangles: count.nullable(),
});
export const productGeometrySchema = z.object({
    fileFormat: z.string(),
    analysisLevel: z.string(),
    triangles: count.nullable(),
    bboxMm: z.tuple([z.number(), z.number(), z.number()]).nullable(),
    unit: z.string(),
    unitCheck: z.string(),
});
export const productListingSchema = z.object({
    listingId: z.string(),
    title: z.string(),
    state: z.string(),
    priceCents: count.nullable(),
    url: z.string().nullable(),
    /** The Etsy listing text as the reader last synced it (v0.1.72); null when Etsy gave none. */
    description: z.string().nullable(),
});
export const productVehicleSchema = z.object({
    make: z.string(),
    model: z.string(),
    generation: z.string(),
    variant: z.string(),
    yearFrom: z.number().int().nullable(),
    yearTo: z.number().int().nullable(),
});
export const productCardSchema = z.object({
    productId: z.string(),
    sku: z.string(),
    title: z.string(),
    status: z.enum(PRODUCT_STATUSES),
    productType: z.enum(PRODUCT_TYPES),
    coverage: z.enum(PRODUCT_COVERAGES),
    formats: z.array(z.string()),
    polyCount: count.nullable(),
    priceCents: count.nullable(),
    fileSizeMb: z.number().nonnegative().nullable(),
    compatibleYearFrom: z.number().int().nullable(),
    compatibleYearTo: z.number().int().nullable(),
    vehicle: productVehicleSchema.nullable(),
    /** The package note ("Complete double cab with underbody."), when the card has one. */
    contents: z.string().nullable(),
    /** The archive review's one-line product note about what is and is not included. */
    coverageNote: z.string().nullable(),
    files: z.array(productFileSchema),
    /** Renders first, then previews and photos, oldest first. */
    images: z.array(productImageSchema),
    geometry: productGeometrySchema.nullable(),
    listing: productListingSchema.nullable(),
    updatedAt: instant,
});
export const productsViewSchema = z.object({
    asOf: instant,
    counts: z.object({
        total: count,
        active: count,
        draft: count,
        inactive: count,
        retired: count,
        /** Products with at least one image. */
        withImages: count,
        images: count,
    }),
    /** Active first, then drafts, then the rest; within a status by make, model and sku. */
    products: z.array(productCardSchema),
});
/** Query of GET /media/products/{assetId}: the size, thumbnail or full; full when absent. */
export const productImageQuerySchema = z.strictObject({
    size: z.enum(PRODUCT_IMAGE_SIZES).default('full'),
});
/** "Toyota Hilux N80 (AN120/AN130), 2015-2020": the vehicle line of a card. */
export function productVehicleLabel(vehicle) {
    if (vehicle === null)
        return null;
    const name = [vehicle.make, vehicle.model, vehicle.generation, vehicle.variant]
        .map((part) => part.trim())
        .filter((part) => part !== '')
        .join(' ');
    const years = vehicle.yearFrom === null && vehicle.yearTo === null
        ? ''
        : vehicle.yearFrom !== null && vehicle.yearTo !== null && vehicle.yearFrom !== vehicle.yearTo
            ? `${vehicle.yearFrom}-${vehicle.yearTo}`
            : String(vehicle.yearFrom ?? vehicle.yearTo);
    return years === '' ? name : `${name}, ${years}`;
}
//# sourceMappingURL=products.js.map